import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  MediaOrderedDetails,
  ItemNoAndType,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { MediasQuantity } from "../../../MediasQuantity/MediasQuantity";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface MediasQuantityCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface MediasQuantityCreatorFragmentProps {
  configProps: MediasQuantityCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

const MediasQuantityCreatorFragment = ({
  configProps,
  commonProps,
}: MediasQuantityCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;
  const storeId = useStoreId();

  const mediasWithPieces = validateInitialDetailsFieldValue<
    MediaOrderedDetails[]
  >(getByPath(task, field), field, taskType);

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const initializeMediasOrderedDetails = useEffectEvent(() => {
    if (!Array.isArray(mediasWithPieces) || isLoading(mediasWithPieces)) {
      return;
    }

    const hasOrderedFields = mediasWithPieces.some(
      (media) => "pieces_ordered" in media || "ptags_ordered" in media
    );

    if (!hasOrderedFields) {
      const mediasWithOrderedDetails = mediasWithPieces.map((media) => ({
        ...media,
        pieces_ordered: media.pieces,
        ptags_ordered: media.pieces,
      }));

      onTaskChange({ details: { medias: mediasWithOrderedDetails } });
    }
  });

  useEffect(() => {
    initializeMediasOrderedDetails();
  }, []);

  const initialMediasOrderedDetails: MediaOrderedDetails[] | null =
    Array.isArray(mediasWithPieces)
      ? mediasWithPieces.map((media) => ({
          ...media,
          pieces_ordered: media.pieces,
          ptags_ordered: media.pieces,
        }))
      : null;

  const [mediasOrderedDetails, setMediasOrderedDetails] = useState<
    MediaOrderedDetails[] | null | Loading
  >(initialMediasOrderedDetails);

  const handlePiecesChange = (
    mediaId: string,
    articleId: string,
    newPieces: number
  ) => {
    setMediasOrderedDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((media) =>
        media.id === mediaId && media.article_id === articleId
          ? { ...media, pieces_ordered: newPieces }
          : media
      );
    });
  };

  const handlePTagsChange = (
    mediaId: string,
    articleId: string,
    newPTags: number
  ) => {
    setMediasOrderedDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((media) =>
        media.id === mediaId && media.article_id === articleId
          ? { ...media, ptags_ordered: newPTags }
          : media
      );
    });
  };

  const handleConfirm = () => {
    if (Array.isArray(mediasOrderedDetails)) {
      onTaskChange({ details: { medias: mediasOrderedDetails } });
    }
  };

  const { data: mediasAndArticleCount, isLoading: isFetchingMedias } =
    useMediasAndArticleCount(
      isLoading(itemNoAndType) ? "" : itemNoAndType?.no,
      storeId
    );

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (
    isFetchingMedias ||
    isLoading(mediasWithPieces) ||
    isLoading(itemNoAndType)
  ) {
    return <LoadingCreatorFragment />;
  }

  // Filter medias to get matching ones from API response
  const matchedMedias =
    mediasAndArticleCount?.filter(
      (media) =>
        Array.isArray(mediasWithPieces) &&
        mediasWithPieces.some(
          (m) => m.id === media.mediaId && m.article_id === media.articleId
        )
    ) ?? [];

  return (
    <MediasQuantity
      mediasOrderedDetails={mediasOrderedDetails}
      mediasData={matchedMedias}
      handlePiecesChange={handlePiecesChange}
      handlePTagsChange={handlePTagsChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default MediasQuantityCreatorFragment;
